/**
 * GA4 wiring. See Trello C-04.
 *
 * The measurement ID is a public identifier — it ships in the page HTML by design,
 * so it is not a secret. It is hardcoded as the default rather than required from
 * the environment on purpose: a missing env var in Vercel would mean analytics
 * silently not loading, which is the exact failure this ticket exists to fix.
 * `NEXT_PUBLIC_GA_ID` still overrides it if a separate property is ever needed.
 */
export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_ID || 'G-QZN8DXFEX7';

type GtagParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    gtag?: (command: string, ...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Send a GA4 event. No-ops safely when gtag has not loaded — ad blockers, consent
 * tooling, and SSR all mean it may legitimately be absent, and analytics must never
 * be able to break a form submission.
 */
export function trackEvent(name: string, params: GtagParams = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  try {
    window.gtag('event', name, params);
  } catch {
    // Deliberately swallowed — a failed analytics call is not worth surfacing.
  }
}

interface LeadEventInput {
  submissionType: 'quote' | 'general';
  locale: string;
  servicesInterested?: string[];
  budget?: string;
  timeline?: string;
}

/**
 * Fires GA4's recommended `generate_lead` event on a genuine contact/quote
 * submission.
 *
 * Only call this on a real server-confirmed success. The form's honeypot branch
 * also shows the success state (to avoid telling a bot it was detected) — firing
 * there would inflate the lead count with bot traffic.
 */
export function trackLead({
  submissionType,
  locale,
  servicesInterested = [],
  budget,
  timeline,
}: LeadEventInput) {
  trackEvent('generate_lead', {
    currency: 'SAR',
    submission_type: submissionType,
    locale,
    // GA4 params must be scalars, so the service list is flattened.
    services_interested: servicesInterested.join(', ') || undefined,
    services_count: servicesInterested.length,
    budget: budget || undefined,
    timeline: timeline || undefined,
  });
}
